import some from "lodash/some";

import {
  ActiveCoachProductsForCustomerQuery,
  BookReviewPageQuery,
} from "@/graphql";
import { UserCoach } from "@/types/coaches";
import { Specialty, MeetingOrReviewProduct } from "@/types/products";
import { ensureNonNullArray } from "@/utils/array";
import { NonNullableArrayItem, Resolve } from "@/utils/types";

const BOOK_MEETING_ANSWER_TYPE = "BOOK_MEETING";

export type MeetingFormField = NonNullableArrayItem<
  MeetingOrReviewProduct["activeFormFields"]
>;

export type BookReviewCoach = NonNullableArrayItem<
  NonNullable<BookReviewPageQuery["currentUser"]>["coaches"]
>;

type CoachProduct = NonNullableArrayItem<
  ActiveCoachProductsForCustomerQuery["activeCoachProductsForCustomer"]
>;

type AgentProfile = BookReviewCoach["agentProfile"] | UserCoach["agentProfile"];

type Coach = Resolve<
  (BookReviewCoach | UserCoach) & {
    agentProfile?:
      | (AgentProfile & {
          canHaveIntroSession?: boolean | null;
        })
      | null;
  }
>;

export function withDisplayedCoachProductInfo(
  products: MeetingOrReviewProduct[],
  coachProducts: CoachProduct[],
): MeetingOrReviewProduct[] {
  if (!products) return [];
  if (coachProducts.length === 0) return products;

  return products.reduce((acc, product) => {
    const coachProduct = coachProducts.find(
      (cp) => cp.product?.id === product.id,
    );

    const { price, locked } = coachProduct || {};
    const { creditPrice } = product;
    const displayedCreditPrice = price || creditPrice;

    acc.push({
      ...product,
      displayedCreditPrice,
      locked,
    });

    return acc;
  }, Object.create([]));
}

interface Mappings {
  COACH_PRODUCTS: Record<string, string[]>;
  COACH_SPECIALTIES: Record<string, string[]>;
  COACH_ID: Record<string, Coach>;
  SPECIALTY_PRODUCTS: Record<string, string[]>;
  SPECIALTY_COACHES: Record<string, string[]>;
  SPECIALTY_ID: Record<string, Specialty>;
  PRODUCT_COACHES: Record<string, string[]>;
  PRODUCT_SPECIALTIES: Record<string, string[]>;
  PRODUCT_ID: Record<string, MeetingOrReviewProduct>;
}

interface GenerateMappingsProps {
  coaches: Coach[];
  specialties: Specialty[];
  products?: MeetingOrReviewProduct[];
}

export function generateMappings({
  coaches,
  specialties,
  products,
}: GenerateMappingsProps): Mappings {
  const mappings: Mappings = {
    COACH_PRODUCTS: {},
    COACH_SPECIALTIES: {},
    COACH_ID: {},
    SPECIALTY_PRODUCTS: {},
    SPECIALTY_COACHES: {},
    SPECIALTY_ID: {},
    PRODUCT_COACHES: {},
    PRODUCT_SPECIALTIES: {},
    PRODUCT_ID: {},
  };

  if (!coaches || !specialties || !products) {
    return mappings;
  }

  specialties.forEach((specialty) => {
    if (!specialty) return;

    mappings["SPECIALTY_PRODUCTS"][specialty.id] = [];
    mappings["SPECIALTY_COACHES"][specialty.id] = [];
    mappings["SPECIALTY_ID"][specialty.id] = specialty;
  });

  products.forEach((product) => {
    if (!product) return;

    mappings["PRODUCT_COACHES"][product.id] = [];
    mappings["PRODUCT_SPECIALTIES"][product.id] = [];
    mappings["PRODUCT_ID"][product.id] = product;

    if (product.specialty) {
      mappings["PRODUCT_SPECIALTIES"][product.id].push(product.specialty.id);
      if (mappings["SPECIALTY_PRODUCTS"][product.specialty.id]) {
        mappings["SPECIALTY_PRODUCTS"][product.specialty.id].push(product.id);
      }
    }
  });

  coaches.forEach((coach) => {
    if (!coach) return;

    const activeProductIds = ensureNonNullArray(coach.activeCoachProducts)
      .map((coachProduct) => coachProduct.product?.id)
      .filter((productId) => productId !== undefined)
      .filter(
        (productId) =>
          coach.agentProfile?.canHaveIntroSession ||
          (mappings["PRODUCT_ID"][productId] &&
            !mappings["PRODUCT_ID"][productId].introProduct),
      );

    mappings["COACH_PRODUCTS"][coach.id] = activeProductIds;
    mappings["COACH_SPECIALTIES"][coach.id] = [];
    mappings["COACH_ID"][coach.id] = coach;

    activeProductIds.forEach((productId) => {
      if (mappings["PRODUCT_COACHES"][productId]) {
        mappings["PRODUCT_COACHES"][productId].push(coach.id);
      }

      if (mappings["PRODUCT_SPECIALTIES"][productId]) {
        mappings["PRODUCT_SPECIALTIES"][productId].forEach((specialtyId) => {
          if (!mappings["COACH_SPECIALTIES"][coach.id].includes(specialtyId)) {
            mappings["COACH_SPECIALTIES"][coach.id].push(specialtyId);
          }

          if (
            mappings["SPECIALTY_COACHES"][specialtyId] &&
            !mappings["SPECIALTY_COACHES"][specialtyId].includes(coach.id)
          ) {
            mappings["SPECIALTY_COACHES"][specialtyId].push(coach.id);
          }
        });
      }
    });
  });

  return mappings;
}

export const checkRequiredField = (
  activeFormFields: MeetingFormField[],
  fieldVadidatorResult: Record<string, boolean>,
) => {
  return !some(
    activeFormFields,
    (formField) => formField.required && !fieldVadidatorResult[formField.id],
  );
};

export const getActiveFormFields = (
  selectedProduct?: MeetingOrReviewProduct,
) => {
  if (!selectedProduct) return [];

  const formFields = ensureNonNullArray(selectedProduct.activeFormFields);
  return formFields.filter(
    (field) => field.answerType !== BOOK_MEETING_ANSWER_TYPE,
  );
};

export const useFormField = ({
  selectedProduct,
  fieldValidatorResult,
}: {
  selectedProduct?: MeetingOrReviewProduct;
  fieldValidatorResult: Record<string, boolean>;
}): {
  activeFormFields: MeetingFormField[];
  isAllRequiredFieldsFilled: boolean;
} => {
  const activeFormFields = getActiveFormFields(selectedProduct);

  const isAllRequiredFieldsFilled = checkRequiredField(
    activeFormFields,
    fieldValidatorResult,
  );

  return { activeFormFields, isAllRequiredFieldsFilled };
};
